/* ---------------- Users tab ---------------- */
const UsersTab = ({ currentUser }) => {
  const qc = useQueryClient();
  const { data: users = [] } = useQuery({ queryKey: ["admin-users"], queryFn: () => api.get("/admin/users").then((r) => r.data) });
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({ email: "", name: "", role: "editor", password: "" });
  const [showNewPass, setShowNewPass] = useState(false);
  const [pwEdit, setPwEdit] = useState(null);
  const [newPw, setNewPw] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [cpOpen, setCpOpen] = useState(false);
  const [cp, setCp] = useState({ current_password: "", new_password: "" });
  const [showCp1, setShowCp1] = useState(false);
  const [showCp2, setShowCp2] = useState(false);
  const isAdmin = currentUser?.role === "admin";

  const addUser = async () => {
    try {
      await api.post("/admin/users", form);
      qc.invalidateQueries({ queryKey: ["admin-users"] });
      setShowAdd(false);
      setForm({ email: "", name: "", role: "editor", password: "" });
      toast.success("User added.");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const setRole = async (email, role) => {
    try {
      await api.put(`/admin/users/${email}/role`, { role });
      qc.invalidateQueries({ queryKey: ["admin-users"] });
      toast.success("Role updated.");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const savePw = async () => {
    try {
      await api.put(`/admin/users/${pwEdit}/password`, { password: newPw });
      setPwEdit(null);
      setNewPw("");
      toast.success("Password updated.");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const removeUser = async (email) => {
    try {
      await api.delete(`/admin/users/${email}`);
      qc.invalidateQueries({ queryKey: ["admin-users"] });
      toast.success("User removed.");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const changeOwnPw = async () => {
    try {
      await api.put("/auth/change-password", cp);
      setCpOpen(false);
      setCp({ current_password: "", new_password: "" });
      toast.success("Your password was changed.");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  if (!isAdmin) {
    return (
      <div className="max-w-md" data-testid="users-tab-editor-view">
        <p className="text-sm text-seastone mb-5">Only admins manage users. You can change your own password below.</p>
        <button onClick={() => setCpOpen(true)} data-testid="open-change-password" className="rounded-full bg-ink text-sand text-sm px-5 py-2.5 hover:bg-azure">
          Change my password
        </button>
        {cpOpen && <ChangePwModal cp={cp} setCp={setCp} showCp1={showCp1} setShowCp1={setShowCp1} showCp2={showCp2} setShowCp2={setShowCp2} onSave={changeOwnPw} onClose={() => setCpOpen(false)} />}
      </div>
    );
  }

  return (
    <div data-testid="admin-users-tab">
      <div className="flex justify-between items-center mb-5">
        <p className="text-sm text-seastone">{users.length} user(s)</p>
        <div className="flex gap-2">
          <button onClick={() => setCpOpen(true)} data-testid="open-change-password" className="rounded-full border border-ink/20 text-xs px-4 py-2.5 hover:border-ink/40">
            Change my password
          </button>
          <button onClick={() => setShowAdd(true)} data-testid="user-add-button" className="inline-flex items-center gap-1.5 rounded-full bg-ink text-sand text-xs px-4 py-2.5 hover:bg-azure">
            <Plus size={14} /> Add User
          </button>
        </div>
      </div>

      <div className="space-y-3">
        {users.map((u) => (
          <div key={u.email} className="bg-white rounded-2xl border border-ink/10 p-4 flex flex-wrap items-center gap-3 justify-between" data-testid={`admin-user-${u.email.replace(/[^a-z0-9]/gi, "-")}`}>
            <div>
              <p className="font-medium text-sm">{u.name || u.email}</p>
              <p className="text-xs text-seastone">{u.email}</p>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <select
                value={u.role}
                onChange={(e) => setRole(u.email, e.target.value)}
                data-testid={`user-role-${u.email.replace(/[^a-z0-9]/gi, "-")}`}
                className="rounded-lg border border-ink/15 bg-white px-2.5 py-2 text-xs focus:outline-none focus:ring-2 focus:ring-azure/40"
                disabled={u.email === currentUser.email}
              >
                <option value="admin">Admin</option>
                <option value="editor">Editor</option>
              </select>
              <button onClick={() => { setPwEdit(u.email); setNewPw(""); }} data-testid={`user-reset-pw-${u.email.replace(/[^a-z0-9]/gi, "-")}`} className="text-xs text-azure hover:underline">
                Set password
              </button>
              {u.email !== currentUser.email && (
                <button onClick={() => removeUser(u.email)} data-testid={`user-delete-${u.email.replace(/[^a-z0-9]/gi, "-")}`} className="text-xs text-coral hover:underline">
                  Delete
                </button>
              )}
            </div>
          </div>
        ))}
      </div>

      {showAdd && (
        <div className="fixed inset-0 z-50 bg-ink/60 backdrop-blur-sm overflow-y-auto p-4" data-lenis-prevent data-testid="user-add-modal">
          <div className="bg-sand rounded-3xl max-w-sm w-full mx-auto my-6 p-6 space-y-3">
            <h3 className="font-serif text-xl">Add User</h3>
            <Field label="Email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="user-form-email" />
            <Field label="Name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="user-form-name" />
            <div>
              <label className={labelCls}>Role</label>
              <select value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} className={inputCls} data-testid="user-form-role">
                <option value="editor">Editor (manage content)</option>
                <option value="admin">Admin (full access + users)</option>
              </select>
            </div>
            <div>
              <label className={labelCls}>Password (min 8 characters)</label>
              <div className="relative">
                <input type={showNewPass ? "text" : "password"} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className={`${inputCls} pr-11`} data-testid="user-form-password" />
                <button type="button" onClick={() => setShowNewPass(!showNewPass)} data-testid="user-form-password-toggle" className="absolute right-3 top-1/2 -translate-y-1/2 text-seastone hover:text-ink" aria-label="Toggle password visibility">
                  {showNewPass ? <EyeOff size={15} /> : <Eye size={15} />}
                </button>
              </div>
            </div>
            <div className="flex gap-3 pt-2">
              <button onClick={addUser} data-testid="user-form-save" className="rounded-full bg-ink text-sand text-sm px-6 py-2.5 hover:bg-azure">Add</button>
              <button onClick={() => setShowAdd(false)} data-testid="user-form-cancel" className="rounded-full border border-ink/20 text-sm px-6 py-2.5">Cancel</button>
            </div>
          </div>
        </div>
      )}

      {pwEdit && (
        <div className="fixed inset-0 z-50 bg-ink/60 backdrop-blur-sm overflow-y-auto p-4" data-lenis-prevent data-testid="user-pw-modal">
          <div className="bg-sand rounded-3xl max-w-sm w-full mx-auto my-6 p-6 space-y-3">
            <h3 className="font-serif text-xl">Set password for {pwEdit}</h3>
            <div>
              <label className={labelCls}>New password (min 8 characters)</label>
              <div className="relative">
                <input type={showPw ? "text" : "password"} value={newPw} onChange={(e) => setNewPw(e.target.value)} className={`${inputCls} pr-11`} data-testid="user-pw-input" />
                <button type="button" onClick={() => setShowPw(!showPw)} data-testid="user-pw-toggle" className="absolute right-3 top-1/2 -translate-y-1/2 text-seastone hover:text-ink" aria-label="Toggle password visibility">
                  {showPw ? <EyeOff size={15} /> : <Eye size={15} />}
                </button>
              </div>
            </div>
            <div className="flex gap-3 pt-2">
              <button onClick={savePw} data-testid="user-pw-save" className="rounded-full bg-ink text-sand text-sm px-6 py-2.5 hover:bg-azure">Save</button>
              <button onClick={() => setPwEdit(null)} data-testid="user-pw-cancel" className="rounded-full border border-ink/20 text-sm px-6 py-2.5">Cancel</button>
            </div>
          </div>
        </div>
      )}

      {cpOpen && <ChangePwModal cp={cp} setCp={setCp} showCp1={showCp1} setShowCp1={setShowCp1} showCp2={showCp2} setShowCp2={setShowCp2} onSave={changeOwnPw} onClose={() => setCpOpen(false)} />}
    </div>
  );
};

const ChangePwModal = ({ cp, setCp, showCp1, setShowCp1, showCp2, setShowCp2, onSave, onClose }) => (
  <div className="fixed inset-0 z-50 bg-ink/60 backdrop-blur-sm overflow-y-auto p-4" data-lenis-prevent data-testid="change-password-modal">
    <div className="bg-sand rounded-3xl max-w-sm w-full mx-auto my-6 p-6 space-y-3">
      <h3 className="font-serif text-xl">Change my password</h3>
      <div>
        <label className={labelCls}>Current password</label>
        <div className="relative">
          <input type={showCp1 ? "text" : "password"} value={cp.current_password} onChange={(e) => setCp({ ...cp, current_password: e.target.value })} className={`${inputCls} pr-11`} data-testid="change-pw-current" />
          <button type="button" onClick={() => setShowCp1(!showCp1)} data-testid="change-pw-current-toggle" className="absolute right-3 top-1/2 -translate-y-1/2 text-seastone hover:text-ink" aria-label="Toggle password visibility">
            {showCp1 ? <EyeOff size={15} /> : <Eye size={15} />}
          </button>
        </div>
      </div>
      <div>
        <label className={labelCls}>New password (min 8 characters)</label>
        <div className="relative">
          <input type={showCp2 ? "text" : "password"} value={cp.new_password} onChange={(e) => setCp({ ...cp, new_password: e.target.value })} className={`${inputCls} pr-11`} data-testid="change-pw-new" />
          <button type="button" onClick={() => setShowCp2(!showCp2)} data-testid="change-pw-new-toggle" className="absolute right-3 top-1/2 -translate-y-1/2 text-seastone hover:text-ink" aria-label="Toggle password visibility">
            {showCp2 ? <EyeOff size={15} /> : <Eye size={15} />}
          </button>
        </div>
      </div>
      <div className="flex gap-3 pt-2">
        <button onClick={onSave} data-testid="change-pw-save" className="rounded-full bg-ink text-sand text-sm px-6 py-2.5 hover:bg-azure">Save</button>
        <button onClick={onClose} data-testid="change-pw-cancel" className="rounded-full border border-ink/20 text-sm px-6 py-2.5">Cancel</button>
      </div>
    </div>
  </div>
);

/* ---------------- Dashboard shell ---------------- */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Eye, EyeOff, ImagePlus, LogOut, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail, LOGO_FULL } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const inputCls =
  "w-full rounded-xl border border-ink/15 bg-white px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-azure/40";
const labelCls = "block text-[10px] font-mono uppercase tracking-[0.2em] text-seastone mb-1.5";

const REVIEW_SOURCES = [
  "Google",
  "Booking.com",
  "Expedia",
  "Agoda",
  "Trip.com",
  "Tripadvisor",
  "Traveloka",
  "Tiket.com",
  "Airbnb",
  "Direct / WhatsApp",
  "Other OTA",
];

const Field = ({ label, ...props }) => (
  <div>
    <label className={labelCls}>{label}</label>
    <input {...props} className={inputCls} />
  </div>
);

const ImageUpload = ({ onUploaded, testid, accept = "image/jpeg,image/png,image/webp,image/gif", label = "Upload photo" }) => {
  const [busy, setBusy] = useState(false);
  const pick = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const fd = new FormData();
    fd.append("file", f);
    setBusy(true);
    try {
      const { data } = await api.post("/admin/upload", fd);
      onUploaded(data.url);
      toast.success("Media uploaded.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
      e.target.value = "";
    }
  };
  return (
    <label
      className={`inline-flex items-center gap-1.5 rounded-full border border-azure/40 text-azure text-xs px-4 py-2 cursor-pointer transition-colors hover:bg-azure hover:text-sand ${busy ? "opacity-60 pointer-events-none" : ""}`}
    >
      <ImagePlus size={13} /> {busy ? "Uploading…" : label}
      <input type="file" accept={accept} hidden onChange={pick} data-testid={testid} />
    </label>
  );
};

const mediaType = (url) => (/\.(mp4|webm|mov)(\?|#|$)/i.test(url) ? "video" : "image");

const TABS = [
  { id: "settings", label: "Settings & Links" },
  { id: "rooms", label: "Rooms & Rates" },
  { id: "boats", label: "Boat Schedule" },
  { id: "journal", label: "Journal" },
  { id: "reviews", label: "Guest Reviews" },
  { id: "messages", label: "Messages" },
  { id: "users", label: "Users" },
];

const PassInput = ({ value, onChange, testid, placeholder = "Minimum 8 characters" }) => {
  const [show, setShow] = useState(false);
  return (
    <div className="relative">
      <input
        type={show ? "text" : "password"}
        value={value}
        onChange={onChange}
        data-testid={testid}
        placeholder={placeholder}
        className={`${inputCls} pr-10`}
      />
      <button
        type="button"
        onClick={() => setShow(!show)}
        data-testid={`${testid}-toggle`}
        aria-label={show ? "Hide password" : "Show password"}
        className="absolute right-3 top-1/2 -translate-y-1/2 text-seastone hover:text-ink transition-colors"
      >
        {show ? <EyeOff size={15} /> : <Eye size={15} />}
      </button>
    </div>
  );
};

/* ---------------- Settings tab ---------------- */
const SettingsTab = () => {
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ["settings"], queryFn: () => api.get("/settings").then((r) => r.data) });
  const [form, setForm] = useState(null);
  useEffect(() => { if (data && !form) setForm({ hero_slides: [], ...data }); }, [data, form]);
  if (!form) return <p className="text-sm text-seastone">Loading…</p>;

  const slides = form.hero_slides || [];
  const seo = form.seo || {};
  const setSeo = (patch) => setForm({ ...form, seo: { ...seo, ...patch } });

  const addSlide = (url) => {
    if (!url) return;
    if (slides.length >= 10) {
      toast.error("Maximum 10 hero slides.");
      return;
    }
    setForm({ ...form, hero_slides: [...slides, { type: mediaType(url), url }] });
  };

  const save = async () => {
    try {
      await api.put("/admin/settings", form);
      qc.invalidateQueries({ queryKey: ["settings"] });
      toast.success("Settings saved.");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  return (
    <div className="space-y-4 max-w-2xl" data-testid="admin-settings-tab">
      <div>
        <label className={labelCls}>Hero slideshow — photos or videos (max 10)</label>
        <div className="flex flex-wrap gap-2 mb-2">
          {slides.map((s, i) => (
            <div key={i} className="space-y-1" data-testid={`hero-slide-chip-${i}`}>
              <div className="relative">
                {s.type === "video" ? (
                  <video src={s.url} muted className="h-16 w-24 rounded-lg object-cover border border-ink/10" />
                ) : (
                  <img src={s.url} alt={s.alt || ""} className="h-16 w-24 rounded-lg object-cover border border-ink/10" />
                )}
                <button
                  onClick={() => setForm({ ...form, hero_slides: slides.filter((_, j) => j !== i) })}
                  className="absolute -top-1.5 -right-1.5 h-5 w-5 grid place-items-center rounded-full bg-coral text-white text-[10px]"
                  data-testid={`hero-slide-remove-${i}`}
                  aria-label="Remove slide"
                >
                  ×
                </button>
                {s.type === "video" && (
                  <span className="absolute bottom-1 left-1 bg-ink/80 text-sand text-[8px] font-mono px-1.5 py-0.5 rounded">VIDEO</span>
                )}
              </div>
              <input
                placeholder="Alt text (SEO)"
                value={s.alt || ""}
                onChange={(e) => setForm({ ...form, hero_slides: slides.map((x, j) => (j === i ? { ...x, alt: e.target.value } : x)) })}
                className="w-24 rounded-md border border-ink/15 bg-white px-1.5 py-1 text-[10px] focus:outline-none focus:ring-1 focus:ring-azure/40"
                data-testid={`hero-slide-alt-${i}`}
              />
            </div>
          ))}
          {slides.length === 0 && <p className="text-xs text-seastone">No slides yet — the default pool photo is shown.</p>}
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <ImageUpload
            accept="image/jpeg,image/png,image/webp,image/gif,video/mp4,video/webm,video/quicktime"
            label="Upload photo / video"
            testid="hero-media-upload"
            onUploaded={addSlide}
          />
          <input
            placeholder="…or paste media URL"
            className={`${inputCls} flex-1 min-w-[180px]`}
            value=""
            onChange={(e) => e.target.value && addSlide(e.target.value)}
            data-testid="hero-slide-url-input"
          />
          <span className="text-[10px] font-mono text-seastone" data-testid="hero-slides-count">{slides.length}/10</span>
        </div>
        <p className="mt-1.5 text-[11px] text-seastone">Slides crossfade every ~6 seconds. Videos play muted on loop. Remember to Save Settings.</p>
      </div>

      <Field label="Alaric booking engine link (all Reserve Now buttons)" value={form.booking_url || ""} onChange={(e) => setForm({ ...form, booking_url: e.target.value })} data-testid="settings-booking-url-input" />
      <Field label="WhatsApp number (digits only, e.g. 6281339192688)" value={form.whatsapp || ""} onChange={(e) => setForm({ ...form, whatsapp: e.target.value })} data-testid="settings-whatsapp-input" />
      <div>
        <label className={labelCls}>WhatsApp pre-filled message</label>
        <textarea rows={2} value={form.whatsapp_message || ""} onChange={(e) => setForm({ ...form, whatsapp_message: e.target.value })} className={inputCls} data-testid="settings-whatsapp-message-input" />
      </div>
      <Field label="Contact email" value={form.email || ""} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="settings-email-input" />
      <Field label="Instagram URL" value={form.instagram || ""} onChange={(e) => setForm({ ...form, instagram: e.target.value })} data-testid="settings-instagram-input" />
      <Field label="Facebook URL" value={form.facebook || ""} onChange={(e) => setForm({ ...form, facebook: e.target.value })} data-testid="settings-facebook-input" />
      <Field label="Address" value={form.address || ""} onChange={(e) => setForm({ ...form, address: e.target.value })} data-testid="settings-address-input" />
      <div className="rounded-2xl border border-ink/10 bg-white p-4 space-y-3" data-testid="location-settings-block">
        <p className="text-sm font-medium">Location & Directions</p>
        <div className="grid grid-cols-2 gap-3">
          <Field
            label="Latitude"
            value={form.maps_lat || ""}
            onChange={(e) => {
              const lat = e.target.value;
              setForm({ ...form, maps_lat: lat, maps_url: lat && form.maps_lng ? `https://maps.google.com/?q=${lat},${form.maps_lng}` : form.maps_url });
            }}
            placeholder="-8.6641"
            data-testid="settings-maps-lat-input"
          />
          <Field
            label="Longitude"
            value={form.maps_lng || ""}
            onChange={(e) => {
              const lng = e.target.value;
              setForm({ ...form, maps_lng: lng, maps_url: form.maps_lat && lng ? `https://maps.google.com/?q=${form.maps_lat},${lng}` : form.maps_url });
            }}
            placeholder="115.4536"
            data-testid="settings-maps-lng-input"
          />
        </div>
        {form.maps_lat && form.maps_lng && (
          <p className="text-[11px] font-mono text-azure" data-testid="settings-maps-preview">
            → maps.google.com/?q={form.maps_lat},{form.maps_lng}
          </p>
        )}
        <Field label="Or paste a full Google Maps link instead (overrides coordinates)" value={form.maps_url || ""} onChange={(e) => setForm({ ...form, maps_url: e.target.value })} data-testid="settings-maps-input" />
        <p className="text-[11px] text-seastone">This pin opens when visitors click Get directions or the map icon. Tip: in Google Maps, right-click the exact spot → the top line shows its coordinates.</p>
      </div>

      <div className="rounded-2xl border border-ink/10 bg-white p-4 space-y-3" data-testid="seo-settings-block">
        <p className="text-sm font-medium">SEO & Social Share</p>
        <Field label="Page title (Google + browser tab)" value={seo.title || ""} onChange={(e) => setSeo({ title: e.target.value })} data-testid="seo-title-input" />
        <div>
          <label className={labelCls}>Meta description (Google snippet & share text)</label>
          <textarea rows={2} value={seo.description || ""} onChange={(e) => setSeo({ description: e.target.value })} className={inputCls} data-testid="seo-description-input" />
        </div>
        <Field label="Keywords (comma separated)" value={seo.keywords || ""} onChange={(e) => setSeo({ keywords: e.target.value })} data-testid="seo-keywords-input" />
        <div>
          <label className={labelCls}>Share image (WhatsApp / social preview)</label>
          <div className="flex items-center gap-2">
            {seo.og_image && <img src={seo.og_image} alt="Share preview" className="h-14 w-24 rounded-lg object-cover border border-ink/10" data-testid="seo-og-image-preview" />}
            <ImageUpload testid="seo-og-image-upload" onUploaded={(url) => setSeo({ og_image: url })} />
            <input placeholder="…or paste image URL" className={`${inputCls} flex-1`} value="" onChange={(e) => e.target.value && setSeo({ og_image: e.target.value })} data-testid="seo-og-image-input" />
          </div>
        </div>
        <p className="text-[11px] text-seastone">Every image alt text set in Rooms, Boats, Journal and Hero slides is used by Google to index your photos. Remember to Save Settings.</p>
      </div>
      <Field label="Boat schedule source URL" value={form.boat_source_url || ""} onChange={(e) => setForm({ ...form, boat_source_url: e.target.value })} data-testid="settings-boat-source-input" />
      <div className="rounded-2xl border border-ink/10 bg-white p-4 space-y-3" data-testid="popup-settings-block">
        <label className="flex items-center gap-2 text-sm font-medium">
          <input
            type="checkbox"
            checked={form.popup?.enabled || false}
            onChange={(e) => setForm({ ...form, popup: { ...(form.popup || {}), enabled: e.target.checked } })}
            data-testid="popup-enabled-toggle"
          />
          Show promo popup on the website
        </label>
        {(form.popup?.enabled) && (
          <>
            <Field label="Popup title" value={form.popup?.title || ""} onChange={(e) => setForm({ ...form, popup: { ...form.popup, title: e.target.value } })} data-testid="popup-title-input" />
            <div>
              <label className={labelCls}>Popup text</label>
              <textarea rows={2} value={form.popup?.text || ""} onChange={(e) => setForm({ ...form, popup: { ...form.popup, text: e.target.value } })} className={inputCls} data-testid="popup-text-input" />
            </div>
            <div>
              <label className={labelCls}>Popup image</label>
              <div className="flex items-center gap-2">
                {form.popup?.image && <img src={form.popup.image} alt="" className="h-14 w-20 rounded-lg object-cover border border-ink/10" data-testid="popup-image-preview" />}
                <ImageUpload testid="popup-image-upload" onUploaded={(url) => setForm({ ...form, popup: { ...form.popup, image: url } })} />
                <input placeholder="…or paste image URL" className={`${inputCls} flex-1`} value="" onChange={(e) => e.target.value && setForm({ ...form, popup: { ...form.popup, image: e.target.value } })} data-testid="popup-image-url-input" />
                {form.popup?.image && (
                  <button onClick={() => setForm({ ...form, popup: { ...form.popup, image: "" } })} data-testid="popup-image-remove" className="text-xs text-coral hover:underline shrink-0">Remove</button>
                )}
              </div>
            </div>
            <div className="grid sm:grid-cols-2 gap-3">
              <Field label="Button label" value={form.popup?.button_label || ""} onChange={(e) => setForm({ ...form, popup: { ...form.popup, button_label: e.target.value } })} data-testid="popup-button-label-input" placeholder="Book Now" />
              <Field label="Button link (empty = booking engine link above)" value={form.popup?.button_url || ""} onChange={(e) => setForm({ ...form, popup: { ...form.popup, button_url: e.target.value } })} data-testid="popup-button-url-input" placeholder="https://…" />
            </div>
            <p className="text-[11px] text-seastone">The popup appears once per visitor session. Remember to Save Settings.</p>
          </>
        )}
      </div>

      <button onClick={save} data-testid="settings-save-button" className="rounded-full bg-ink text-sand text-sm px-6 py-3 hover:bg-azure transition-colors">Save Settings</button>
    </div>
  );
};

/* ---------------- Rooms tab ---------------- */
const emptyRoom = { name: "", tagline: "", description: "", price_label: "", size: "", sleeps: "", features: [], images: [], blocked_ranges: [], order: 0 };

const RoomsTab = () => {
  const qc = useQueryClient();
  const { data: rooms = [] } = useQuery({ queryKey: ["rooms"], queryFn: () => api.get("/rooms").then((r) => r.data) });
  const [editing, setEditing] = useState(null);
  const [range, setRange] = useState({ start: "", end: "", note: "" });

  const save = async () => {
    try {
      const payload = {
        ...editing,
        features: (editing.features || []).filter(Boolean),
        images: (editing.images || []).filter(Boolean),
        image_alts: editing.image_alts || [],
        blocked_ranges: editing.blocked_ranges || [],
      };
      if (editing.id) await api.put(`/admin/rooms/${editing.id}`, payload);
      else await api.post("/admin/rooms", payload);
      qc.invalidateQueries({ queryKey: ["rooms"] });
      setEditing(null);
      toast.success("Room saved.");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const remove = async (id) => {
    await api.delete(`/admin/rooms/${id}`);
    qc.invalidateQueries({ queryKey: ["rooms"] });
    toast.success("Room deleted.");
  };

  const addRange = () => {
    if (!range.start || !range.end) return;
    setEditing({ ...editing, blocked_ranges: [...(editing.blocked_ranges || []), { ...range }] });
    setRange({ start: "", end: "", note: "" });
  };

  const syncIcal = async () => {
    if (!editing.id) return;
    try {
      const { data } = await api.post(`/admin/rooms/${editing.id}/ical-sync`);
      qc.invalidateQueries({ queryKey: ["rooms"] });
      setEditing((prev) => ({ ...prev, ical_synced_at: data.synced_at }));
      toast.success(`Calendar synced — ${data.synced_ranges} booked period(s) imported.`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  return (
    <div data-testid="admin-rooms-tab">
      <div className="flex justify-between items-center mb-5">
        <p className="text-sm text-seastone">{rooms.length} stay options</p>
        <button onClick={() => setEditing({ ...emptyRoom })} data-testid="room-add-button" className="inline-flex items-center gap-1.5 rounded-full bg-ink text-sand text-xs px-4 py-2.5 hover:bg-azure">
          <Plus size={14} /> Add Room
        </button>
      </div>
      <div className="grid md:grid-cols-2 gap-4">
        {rooms.map((r) => (
          <div key={r.id} className="bg-white rounded-2xl border border-ink/10 p-4 flex gap-4" data-testid={`admin-room-${r.id}`}>
            <img src={r.images?.[0]} alt={r.name} className="h-20 w-24 rounded-xl object-cover shrink-0" />
            <div className="flex-1 min-w-0">
              <p className="font-serif text-lg leading-tight truncate">{r.name}</p>
              <p className="text-xs text-seastone mt-1">{r.price_label}</p>
              <p className="text-[10px] font-mono text-seastone/70 mt-1">{(r.blocked_ranges || []).length} blocked period(s)</p>
              <div className="mt-2 flex gap-2">
                <button onClick={() => setEditing({ ...emptyRoom, ...r, blocked_ranges: r.blocked_ranges || [] })} data-testid={`room-edit-${r.id}`} className="text-xs text-azure hover:underline">Edit</button>
                <button onClick={() => remove(r.id)} data-testid={`room-delete-${r.id}`} className="text-xs text-coral hover:underline">Delete</button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {editing && (
        <div className="fixed inset-0 z-50 bg-ink/60 backdrop-blur-sm overflow-y-auto p-4" data-lenis-prevent data-testid="room-edit-modal">
          <div className="bg-sand rounded-3xl max-w-xl w-full mx-auto my-6">
            <div className="sticky top-0 z-10 bg-sand rounded-t-3xl px-6 pt-5 pb-3 border-b border-ink/10 flex items-center justify-between">
              <h3 className="font-serif text-xl">{editing.id ? "Edit Room" : "Add Room"}</h3>
              <button onClick={() => setEditing(null)} data-testid="room-form-close-x" className="text-seastone hover:text-ink text-xl leading-none px-2" aria-label="Close">×</button>
            </div>
            <div className="p-6 space-y-3">
            <Field label="Name" value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} data-testid="room-form-name" />
            <Field label="Tagline" value={editing.tagline} onChange={(e) => setEditing({ ...editing, tagline: e.target.value })} data-testid="room-form-tagline" />
            <div>
              <label className={labelCls}>Description</label>
              <textarea rows={3} value={editing.description} onChange={(e) => setEditing({ ...editing, description: e.target.value })} className={inputCls} data-testid="room-form-description" />
            </div>
            <div className="grid grid-cols-3 gap-3">
              <Field label="Price label" value={editing.price_label} onChange={(e) => setEditing({ ...editing, price_label: e.target.value })} data-testid="room-form-price" />
              <Field label="Size" value={editing.size} onChange={(e) => setEditing({ ...editing, size: e.target.value })} data-testid="room-form-size" />
              <Field label="Sleeps" value={editing.sleeps} onChange={(e) => setEditing({ ...editing, sleeps: e.target.value })} data-testid="room-form-sleeps" />
            </div>
            <Field label="Features (comma separated)" value={(editing.features || []).join(", ")} onChange={(e) => setEditing({ ...editing, features: e.target.value.split(",").map((s) => s.trim()) })} data-testid="room-form-features" />

            <div>
              <label className={labelCls}>Photos</label>
              <div className="flex flex-wrap gap-2 mb-2">
                {(editing.images || []).filter(Boolean).map((img, i) => (
                  <div key={i} className="space-y-1" data-testid={`room-image-chip-${i}`}>
                    <div className="relative">
                      <img src={img} alt={(editing.image_alts || [])[i] || ""} className="h-16 w-20 rounded-lg object-cover border border-ink/10" />
                      <button
                        onClick={() => setEditing({ ...editing, images: editing.images.filter((_, j) => j !== i), image_alts: (editing.image_alts || []).filter((_, j) => j !== i) })}
                        className="absolute -top-1.5 -right-1.5 h-5 w-5 grid place-items-center rounded-full bg-coral text-white text-[10px]"
                        data-testid={`room-image-remove-${i}`}
                        aria-label="Remove photo"
                      >
                        ×
                      </button>
                    </div>
                    <input
                      placeholder="Alt text (SEO)"
                      value={(editing.image_alts || [])[i] || ""}
                      onChange={(e) => {
                        const alts = [...(editing.image_alts || [])];
                        alts[i] = e.target.value;
                        setEditing({ ...editing, image_alts: alts });
                      }}
                      className="w-20 rounded-md border border-ink/15 bg-white px-1.5 py-1 text-[10px] focus:outline-none focus:ring-1 focus:ring-azure/40"
                      data-testid={`room-image-alt-${i}`}
                    />
                  </div>
                ))}
              </div>
              <div className="flex items-center gap-2">
                <ImageUpload testid="room-photo-upload" onUploaded={(url) => setEditing({ ...editing, images: [...(editing.images || []), url] })} />
                <input
                  placeholder="…or paste image URL"
                  className={`${inputCls} flex-1`}
                  value=""
                  onChange={(e) => e.target.value && setEditing({ ...editing, images: [...(editing.images || []), e.target.value] })}
                  data-testid="room-form-images"
                />
              </div>
            </div>

            <div>
              <label className={labelCls}>Booked / blocked periods (availability calendar)</label>
              <div className="space-y-2 mb-2">
                {(editing.blocked_ranges || []).map((r, i) => (
                  <div key={i} className="flex items-center justify-between bg-white rounded-lg border border-ink/10 px-3 py-2 text-xs" data-testid={`blocked-range-${i}`}>
                    <span className="font-mono">{r.start} → {r.end}{r.note ? ` · ${r.note}` : ""}</span>
                    <button onClick={() => setEditing({ ...editing, blocked_ranges: editing.blocked_ranges.filter((_, j) => j !== i) })} data-testid={`blocked-range-delete-${i}`} className="text-coral" aria-label="Remove period">
                      <Trash2 size={13} />
                    </button>
                  </div>
                ))}
                {(editing.blocked_ranges || []).length === 0 && <p className="text-xs text-seastone">No blocked periods — every date shows as available.</p>}
              </div>
              <div className="grid grid-cols-[1fr_1fr_auto] gap-2 items-end">
                <Field label="From" type="date" value={range.start} onChange={(e) => setRange({ ...range, start: e.target.value })} data-testid="blocked-range-start" />
                <Field label="To" type="date" value={range.end} onChange={(e) => setRange({ ...range, end: e.target.value })} data-testid="blocked-range-end" />
                <button onClick={addRange} data-testid="blocked-range-add" className="h-[42px] rounded-xl bg-ink text-sand text-xs px-4 hover:bg-azure">Add</button>
              </div>
            </div>

            <div>
              <label className={labelCls}>Auto-sync booked dates from Alaric / OTA (iCal feed URL)</label>
              <div className="flex items-center gap-2">
                <input
                  placeholder="https://…/calendar.ics"
                  value={editing.ical_url || ""}
                  onChange={(e) => setEditing({ ...editing, ical_url: e.target.value })}
                  className={`${inputCls} flex-1`}
                  data-testid="room-form-ical-url"
                />
                <button
                  onClick={syncIcal}
                  disabled={!editing.id || !editing.ical_url}
                  data-testid="room-ical-sync-button"
                  className="rounded-xl bg-azure text-sand text-xs font-medium px-4 py-3 hover:bg-azure-dark disabled:opacity-50 shrink-0"
                  title={editing.id ? "Fetch booked dates now" : "Save the room first"}
                >
                  Sync now
                </button>
              </div>
              <p className="mt-1.5 text-[11px] text-seastone" data-testid="room-ical-status">
                {editing.ical_synced_at
                  ? `Last synced ${new Date(editing.ical_synced_at).toLocaleString()} — re-syncs automatically every 6 hours. Manual periods above are never removed by sync.`
                  : "Paste the iCal export link from your booking engine, save the room, then Sync now."}
              </p>
            </div>

            </div>
            <div className="sticky bottom-0 bg-sand rounded-b-3xl px-6 py-4 border-t border-ink/10 flex gap-3">
              <button onClick={save} data-testid="room-form-save" className="rounded-full bg-ink text-sand text-sm px-6 py-2.5 hover:bg-azure">Save</button>
              <button onClick={() => setEditing(null)} data-testid="room-form-cancel" className="rounded-full border border-ink/20 text-sm px-6 py-2.5">Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

/* ---------------- Boats tab ---------------- */
const BoatsTab = () => {
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ["boats"], queryFn: () => api.get("/boats").then((r) => r.data) });
  const [form, setForm] = useState(null);
  useEffect(() => { if (data && !form) setForm({ ...data, operators: (data.operators || []).map((o) => ({ image: "", ...o })) }); }, [data, form]);
  if (!form) return <p className="text-sm text-seastone">Loading…</p>;

  const setOp = (i, patch) => setForm({ ...form, operators: form.operators.map((o, j) => (j === i ? { ...o, ...patch } : o)) });

  const save = async () => {
    try {
      await api.put("/admin/boats", form);
      qc.invalidateQueries({ queryKey: ["boats"] });
      toast.success("Boat schedule saved.");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  return (
    <div className="space-y-4" data-testid="admin-boats-tab">
      <div>
        <label className={labelCls}>Schedule note</label>
        <textarea rows={2} value={form.updated_note || ""} onChange={(e) => setForm({ ...form, updated_note: e.target.value })} className={inputCls} data-testid="boats-note-input" />
      </div>
      {form.operators.map((o, i) => (
        <div key={i} className="bg-white rounded-2xl border border-ink/10 p-4 space-y-3" data-testid={`boat-edit-${i}`}>
          <div className="flex items-center gap-4">
            <img src={o.image || "https://images.unsplash.com/photo-1638979455225-577905c5ada6?crop=entropy&cs=srgb&fm=jpg&q=85"} alt={o.name} className="h-16 w-24 rounded-xl object-cover border border-ink/10" />
            <div className="flex-1 grid sm:grid-cols-2 gap-3">
              <Field label="Company" value={o.name} onChange={(e) => setOp(i, { name: e.target.value })} data-testid={`boat-name-${i}`} />
              <Field label="Price / adult" value={o.price} onChange={(e) => setOp(i, { price: e.target.value })} data-testid={`boat-price-${i}`} />
            </div>
            <button onClick={() => setForm({ ...form, operators: form.operators.filter((_, j) => j !== i) })} data-testid={`boat-delete-${i}`} className="h-10 w-10 grid place-items-center rounded-xl border border-coral/40 text-coral shrink-0" aria-label="Remove operator">
              <Trash2 size={14} />
            </button>
          </div>
          <div className="flex items-center gap-2">
            <ImageUpload testid={`boat-photo-upload-${i}`} onUploaded={(url) => setOp(i, { image: url })} />
            <input placeholder="…or paste boat photo URL" className={`${inputCls} flex-1`} value="" onChange={(e) => e.target.value && setOp(i, { image: e.target.value })} data-testid={`boat-image-url-${i}`} />
            <input
              placeholder="Alt text (SEO)"
              value={o.image_alt || ""}
              onChange={(e) => setOp(i, { image_alt: e.target.value })}
              className="w-44 rounded-xl border border-ink/15 bg-white px-3 py-2.5 text-xs focus:outline-none focus:ring-2 focus:ring-azure/40"
              data-testid={`boat-image-alt-${i}`}
            />
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            <Field label="Sanur → Lembongan times (comma)" value={(o.to_lembongan || []).join(", ")} onChange={(e) => setOp(i, { to_lembongan: e.target.value.split(",").map((s) => s.trim()).filter(Boolean) })} data-testid={`boat-to-lembongan-${i}`} />
            <Field label="Lembongan → Sanur times (comma)" value={(o.to_sanur || []).join(", ")} onChange={(e) => setOp(i, { to_sanur: e.target.value.split(",").map((s) => s.trim()).filter(Boolean) })} data-testid={`boat-to-sanur-${i}`} />
          </div>
        </div>
      ))}
      <div className="flex gap-3">
        <button onClick={() => setForm({ ...form, operators: [...form.operators, { name: "", price: "", image: "", to_lembongan: [], to_sanur: [] }] })} data-testid="boat-add-button" className="inline-flex items-center gap-1.5 rounded-full border border-ink/20 text-sm px-5 py-2.5">
          <Plus size={14} /> Add Company
        </button>
        <button onClick={save} data-testid="boats-save-button" className="rounded-full bg-ink text-sand text-sm px-6 py-2.5 hover:bg-azure">Save Schedule</button>
      </div>
    </div>
  );
};

/* ---------------- Journal tab ---------------- */
const emptyArticle = { title: "", category: "Island Life", minutes: 4, excerpt: "", body: "", image: "" };

const JournalTab = () => {
  const qc = useQueryClient();
  const { data: articles = [] } = useQuery({ queryKey: ["articles"], queryFn: () => api.get("/articles").then((r) => r.data) });
  const [editing, setEditing] = useState(null);

  const save = async () => {
    try {
      if (editing.id) await api.put(`/admin/articles/${editing.id}`, editing);
      else await api.post("/admin/articles", editing);
      qc.invalidateQueries({ queryKey: ["articles"] });
      setEditing(null);
      toast.success("Article saved.");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const remove = async (id) => {
    await api.delete(`/admin/articles/${id}`);
    qc.invalidateQueries({ queryKey: ["articles"] });
    toast.success("Article deleted.");
  };

  return (
    <div data-testid="admin-journal-tab">
      <div className="flex justify-between items-center mb-5">
        <p className="text-sm text-seastone">{articles.length} articles</p>
        <button onClick={() => setEditing({ ...emptyArticle })} data-testid="article-add-button" className="inline-flex items-center gap-1.5 rounded-full bg-ink text-sand text-xs px-4 py-2.5 hover:bg-azure">
          <Plus size={14} /> Add Article
        </button>
      </div>
      <div className="space-y-3">
        {articles.map((a) => (
          <div key={a.id} className="bg-white rounded-2xl border border-ink/10 p-4 flex items-center gap-4" data-testid={`admin-article-${a.id}`}>
            <img src={a.image} alt={a.title} className="h-14 w-20 rounded-lg object-cover shrink-0" />
            <div className="flex-1 min-w-0">
              <p className="font-serif truncate">{a.title}</p>
              <p className="text-xs text-seastone">{a.category} · {a.minutes} min</p>
            </div>
            <button onClick={() => setEditing({ ...a })} data-testid={`article-edit-${a.id}`} className="text-xs text-azure hover:underline">Edit</button>
            <button onClick={() => remove(a.id)} data-testid={`article-delete-${a.id}`} className="text-xs text-coral hover:underline">Delete</button>
          </div>
        ))}
      </div>

      {editing && (
        <div className="fixed inset-0 z-50 bg-ink/60 backdrop-blur-sm overflow-y-auto p-4" data-lenis-prevent data-testid="article-edit-modal">
          <div className="bg-sand rounded-3xl max-w-xl w-full mx-auto my-6">
            <div className="sticky top-0 z-10 bg-sand rounded-t-3xl px-6 pt-5 pb-3 border-b border-ink/10 flex items-center justify-between">
              <h3 className="font-serif text-xl">{editing.id ? "Edit Article" : "Add Article"}</h3>
              <button onClick={() => setEditing(null)} data-testid="article-form-close-x" className="text-seastone hover:text-ink text-xl leading-none px-2" aria-label="Close">×</button>
            </div>
            <div className="p-6 space-y-3">
            <Field label="Title" value={editing.title} onChange={(e) => setEditing({ ...editing, title: e.target.value })} data-testid="article-form-title" />
            <div className="grid grid-cols-2 gap-3">
              <Field label="Category" value={editing.category} onChange={(e) => setEditing({ ...editing, category: e.target.value })} data-testid="article-form-category" />
              <Field label="Minutes read" type="number" value={editing.minutes} onChange={(e) => setEditing({ ...editing, minutes: Number(e.target.value) })} data-testid="article-form-minutes" />
            </div>
            <div>
              <label className={labelCls}>Cover image</label>
              <div className="flex items-center gap-2">
                {editing.image && <img src={editing.image} alt="" className="h-12 w-16 rounded-lg object-cover border border-ink/10" />}
                <ImageUpload testid="article-photo-upload" onUploaded={(url) => setEditing({ ...editing, image: url })} />
                <input placeholder="…or paste image URL" className={`${inputCls} flex-1`} value="" onChange={(e) => e.target.value && setEditing({ ...editing, image: e.target.value })} data-testid="article-form-image" />
              </div>
            </div>
            <div>
              <label className={labelCls}>Excerpt</label>
              <textarea rows={2} value={editing.excerpt} onChange={(e) => setEditing({ ...editing, excerpt: e.target.value })} className={inputCls} data-testid="article-form-excerpt" />
            </div>
            <div>
              <label className={labelCls}>Body</label>
              <textarea rows={5} value={editing.body} onChange={(e) => setEditing({ ...editing, body: e.target.value })} className={inputCls} data-testid="article-form-body" />
            </div>
            </div>
            <div className="sticky bottom-0 bg-sand rounded-b-3xl px-6 py-4 border-t border-ink/10 flex gap-3">
              <button onClick={save} data-testid="article-form-save" className="rounded-full bg-ink text-sand text-sm px-6 py-2.5 hover:bg-azure">Save</button>
              <button onClick={() => setEditing(null)} data-testid="article-form-cancel" className="rounded-full border border-ink/20 text-sm px-6 py-2.5">Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

/* ---------------- Reviews tab ---------------- */
const emptyReview = { name: "", origin: "", source: "Google", rating: 5, text: "", published: true };

const ReviewsTab = () => {
  const qc = useQueryClient();
  const { data: reviews = [] } = useQuery({ queryKey: ["admin-reviews"], queryFn: () => api.get("/admin/reviews").then((r) => r.data) });
  const [editing, setEditing] = useState(null);

  const save = async () => {
    try {
      if (editing.id) await api.put(`/admin/reviews/${editing.id}`, editing);
      else await api.post("/admin/reviews", editing);
      qc.invalidateQueries({ queryKey: ["admin-reviews"] });
      qc.invalidateQueries({ queryKey: ["reviews"] });
      setEditing(null);
      toast.success("Review saved.");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const remove = async (id) => {
    await api.delete(`/admin/reviews/${id}`);
    qc.invalidateQueries({ queryKey: ["admin-reviews"] });
    qc.invalidateQueries({ queryKey: ["reviews"] });
    toast.success("Review deleted.");
  };

  return (
    <div data-testid="admin-reviews-tab">
      <div className="flex justify-between items-center mb-5">
        <p className="text-sm text-seastone">{reviews.length} reviews</p>
        <button onClick={() => setEditing({ ...emptyReview })} data-testid="review-add-button" className="inline-flex items-center gap-1.5 rounded-full bg-ink text-sand text-xs px-4 py-2.5 hover:bg-azure">
          <Plus size={14} /> Add Review
        </button>
      </div>
      <div className="space-y-3">
        {reviews.map((r) => (
          <div key={r.id} className="bg-white rounded-2xl border border-ink/10 p-4" data-testid={`admin-review-${r.id}`}>
            <div className="flex flex-wrap items-center justify-between gap-2">
              <p className="font-medium text-sm">{r.name} {r.origin ? <span className="text-seastone font-normal">· {r.origin}</span> : null}</p>
              <div className="flex items-center gap-3">
                {r.source && <span className="text-[10px] font-mono uppercase tracking-wider text-azure bg-azure/10 rounded-full px-2.5 py-1" data-testid={`admin-review-source-${r.id}`}>{r.source}</span>}
                <span className="text-xs font-mono text-gold">{"★".repeat(r.rating)}</span>
                <span className={`text-[10px] font-mono uppercase tracking-wider ${r.published ? "text-azure" : "text-seastone"}`}>{r.published ? "Published" : "Hidden"}</span>
                <button onClick={() => setEditing({ ...emptyReview, ...r })} data-testid={`review-edit-${r.id}`} className="text-xs text-azure hover:underline">Edit</button>
                <button onClick={() => remove(r.id)} data-testid={`review-delete-${r.id}`} className="text-xs text-coral hover:underline">Delete</button>
              </div>
            </div>
            <p className="mt-1.5 text-sm text-ink/75 leading-relaxed">{r.text}</p>
          </div>
        ))}
        {reviews.length === 0 && <p className="text-sm text-seastone">No reviews yet — add the first one.</p>}
      </div>

      {editing && (
        <div className="fixed inset-0 z-50 bg-ink/60 backdrop-blur-sm overflow-y-auto p-4" data-lenis-prevent data-testid="review-edit-modal">
          <div className="bg-sand rounded-3xl max-w-md w-full mx-auto my-6">
            <div className="sticky top-0 z-10 bg-sand rounded-t-3xl px-6 pt-5 pb-3 border-b border-ink/10 flex items-center justify-between">
              <h3 className="font-serif text-xl">{editing.id ? "Edit Review" : "Add Review"}</h3>
              <button onClick={() => setEditing(null)} data-testid="review-form-close-x" className="text-seastone hover:text-ink text-xl leading-none px-2" aria-label="Close">×</button>
            </div>
            <div className="p-6 space-y-3">
            <Field label="Guest name" value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} data-testid="review-form-name" />
            <div className="grid grid-cols-2 gap-3">
              <Field label="Origin (e.g. Australia)" value={editing.origin} onChange={(e) => setEditing({ ...editing, origin: e.target.value })} data-testid="review-form-origin" />
              <div>
                <label className={labelCls}>Rating</label>
                <select value={editing.rating} onChange={(e) => setEditing({ ...editing, rating: Number(e.target.value) })} className={inputCls} data-testid="review-form-rating">
                  {[5, 4, 3, 2, 1].map((n) => <option key={n} value={n}>{n} star{n > 1 ? "s" : ""}</option>)}
                </select>
              </div>
            </div>
            <div>
              <label className={labelCls}>Review source</label>
              <select value={editing.source} onChange={(e) => setEditing({ ...editing, source: e.target.value })} className={inputCls} data-testid="review-form-source">
                {REVIEW_SOURCES.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <div>
              <label className={labelCls}>Review text</label>
              <textarea rows={4} value={editing.text} onChange={(e) => setEditing({ ...editing, text: e.target.value })} className={inputCls} data-testid="review-form-text" />
            </div>
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={editing.published} onChange={(e) => setEditing({ ...editing, published: e.target.checked })} data-testid="review-form-published" />
              Show on website
            </label>
            </div>
            <div className="sticky bottom-0 bg-sand rounded-b-3xl px-6 py-4 border-t border-ink/10 flex gap-3">
              <button onClick={save} data-testid="review-form-save" className="rounded-full bg-ink text-sand text-sm px-6 py-2.5 hover:bg-azure">Save</button>
              <button onClick={() => setEditing(null)} data-testid="review-form-cancel" className="rounded-full border border-ink/20 text-sm px-6 py-2.5">Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

/* ---------------- Messages tab ---------------- */
const MessagesTab = () => {
  const qc = useQueryClient();
  const { data: messages = [] } = useQuery({ queryKey: ["admin-messages"], queryFn: () => api.get("/admin/messages").then((r) => r.data) });

  const remove = async (id) => {
    await api.delete(`/admin/messages/${id}`);
    qc.invalidateQueries({ queryKey: ["admin-messages"] });
  };

  return (
    <div className="space-y-3" data-testid="admin-messages-tab">
      {messages.length === 0 && <p className="text-sm text-seastone" data-testid="messages-empty">No enquiries yet.</p>}
      {messages.map((m) => (
        <div key={m.id} className="bg-white rounded-2xl border border-ink/10 p-5" data-testid={`admin-message-${m.id}`}>
          <div className="flex flex-wrap justify-between gap-2">
            <p className="font-medium text-sm">{m.name} · <a href={`mailto:${m.email}`} className="text-azure">{m.email}</a></p>
            <p className="text-[11px] font-mono text-seastone">{new Date(m.created_at).toLocaleString()}</p>
          </div>
          <p className="mt-1 text-[10px] font-mono uppercase tracking-[0.2em] text-azure">{m.subject}</p>
          <p className="mt-2 text-sm text-ink/80 leading-relaxed">{m.body}</p>
          <button onClick={() => remove(m.id)} data-testid={`message-delete-${m.id}`} className="mt-3 text-xs text-coral hover:underline">Delete</button>
        </div>
      ))}
    </div>
  );
};

/* ---------------- Dashboard shell ---------------- */
export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("settings");

  useEffect(() => {
    if (user === false) navigate("/admin/login", { replace: true });
  }, [user, navigate]);

  if (!user) return null;

  return (
    <div className="min-h-screen bg-sand2" data-testid="admin-dashboard">
      <header className="bg-ink text-sand">
        <div className="max-w-6xl mx-auto px-5 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src={LOGO_FULL} alt="Mahadinusa logo" className="h-9" data-testid="admin-logo" />
            <div className="leading-none">
              <p className="font-serif">Admin</p>
              <p className="text-[10px] font-mono uppercase tracking-[0.3em] text-sand/50 mt-1">{user.email}</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <a href="/" data-testid="admin-view-site-link" className="text-xs text-sand/70 hover:text-sand">View site</a>
            <button onClick={logout} data-testid="admin-logout-button" className="inline-flex items-center gap-1.5 rounded-full border border-sand/30 text-xs px-4 py-2 hover:bg-sand/10">
              <LogOut size={13} /> Logout
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-5 py-8 lg:flex lg:gap-8 items-start">
        <aside className="lg:w-56 lg:shrink-0 lg:sticky lg:top-8 mb-6 lg:mb-0">
          <nav className="flex lg:flex-col gap-2 overflow-x-auto pb-2 lg:pb-0" data-testid="admin-tabs">
            {TABS.map((t) => (
              <button
                key={t.id}
                onClick={() => setTab(t.id)}
                data-testid={`admin-tab-${t.id}`}
                className={`whitespace-nowrap lg:whitespace-normal text-left rounded-xl px-4 py-3 text-sm transition-colors shrink-0 lg:shrink ${tab === t.id ? "bg-ink text-sand shadow-[0_8px_24px_rgba(14,27,46,0.25)]" : "bg-white text-ink/70 border border-ink/10 hover:border-ink/30 hover:text-ink"}`}
              >
                {t.label}
              </button>
            ))}
          </nav>
        </aside>
        <div className="flex-1 min-w-0" data-testid="admin-content">
          {tab === "settings" && <SettingsTab />}
          {tab === "rooms" && <RoomsTab />}
          {tab === "boats" && <BoatsTab />}
          {tab === "journal" && <JournalTab />}
          {tab === "reviews" && <ReviewsTab />}
          {tab === "messages" && <MessagesTab />}
          {tab === "users" && <UsersTab currentUser={user} />}
        </div>
      </div>
    </div>
  );
}
